import { TableColumnType, Table } from '@/components';
import { Space, Typography } from 'antd';
import { useMemo } from 'react';
import { PersonalizationTableRowValues } from './PersonalizationDetails';
import styles from './PersonalizationSummary.module.scss';
import { PersonalizationSummarySubTotalInfo } from './PersonalizationSummarySubtotalInfo';
import {
  getItemTotalPriceBySalesTaxType,
  SalesTaxType,
  SpecialRequestStatus,
} from '@/core';
import { PersonalizationTag } from './PersonalizationTag';

interface Props {
  className?: string;
  title?: string;
  subTitle?: string;
  name: string;
  dataSource: PersonalizationTableRowValues[];
  columns: TableColumnType<PersonalizationTableRowValues>[];
  loading?: boolean;
  subtotalTitle?: string;
  showBaseCost?: boolean;
  showResidentPrice?: boolean;
  isPublic?: boolean;
  salesTaxType?: SalesTaxType;
  inactive?: boolean;
}

const Title = Typography.Title;

export const useGetPersonalizationItemsSubtotalAmount = (
  data: PersonalizationTableRowValues[],
  by: (item: PersonalizationTableRowValues) => number | undefined,
  show?: boolean,
  checkSpecialRequests?: boolean,
): { amount?: number; isFinalAmount?: boolean } => {
  return useMemo(() => {
    if (!show) return {};
    const filteredData = checkSpecialRequests
      ? data.filter(
          (x) =>
            !x.specialRequest ||
            (x.specialRequest.status !== SpecialRequestStatus.Denied &&
              x.specialRequest.status !==
                SpecialRequestStatus.ResidentDeclined),
        )
      : data;
    const isFinalAmount = !filteredData.some(
      (x) =>
        x.specialRequest &&
        x.specialRequest.status !== SpecialRequestStatus.ConstructionApproved &&
        x.specialRequest.status !== SpecialRequestStatus.Denied &&
        x.specialRequest.status !== SpecialRequestStatus.ResidentDeclined,
    );

    return {
      amount: filteredData
        ? filteredData.reduce((acc, x) => acc + (by(x) ?? 0), 0)
        : filteredData,
      isFinalAmount,
    };
  }, [data, by, show, checkSpecialRequests]);
};

export function PersonalizationItemList(props: Props) {
  const {
    className,
    dataSource,
    columns,
    title,
    subTitle,
    name,
    subtotalTitle,
    loading,
    showBaseCost,
    showResidentPrice,
    isPublic,
    salesTaxType,
    inactive,
  } = props;

  const { amount: subtotalResidentPriceAmount } =
    useGetPersonalizationItemsSubtotalAmount(
      dataSource,
      (x) =>
        getItemTotalPriceBySalesTaxType(
          {
            residentPriceWithoutTax:
              x.personalizationItem?.pricing?.residentPriceWithoutTax ??
              x.specialRequest?.prices?.residentPriceWithoutTax,
            totalResidentPrice: x.totalResidentPrice,
          },
          salesTaxType,
        ),
      showResidentPrice,
    );

  const { amount: subtotalBaseCostAmount } =
    useGetPersonalizationItemsSubtotalAmount(
      dataSource,
      (x) => x.totalBaseCost,
      showBaseCost,
    );

  const rowClassName = inactive ? styles.inactiveRow : undefined;

  const rowKey = (value: PersonalizationTableRowValues) =>
    value.personalizationItem?.id ?? value.specialRequest?.id ?? '';

  return (
    <Space direction="vertical" size="large" className={className}>
      {title && (
        <Title level={4} className={styles.title}>
          {title}
        </Title>
      )}
      {subTitle && <PersonalizationTag value={subTitle} />}
      <Table<PersonalizationTableRowValues>
        name={name}
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        loading={loading}
        scroll={isPublic ? { x: 100 } : undefined}
        sticky={isPublic ? { offsetHeader: 0 } : undefined}
        rowKey={rowKey}
        rowClassName={rowClassName}
        hideDefaultActions={!isPublic}
        hideExportAction={isPublic}
        hideResetFiltersAction={isPublic}
        columnSettings={isPublic ? 'local' : 'server'}
        responsiveCardView={isPublic}
      />

      {!isPublic && subtotalTitle && (
        <PersonalizationSummarySubTotalInfo
          title={subtotalTitle}
          baseCostSubtotal={subtotalBaseCostAmount}
          residentPriceSubtotal={subtotalResidentPriceAmount}
        />
      )}
    </Space>
  );
}
